"use client";

import React, { Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { IntercomAuthLifecycle } from "../performance/intercom-auth-lifecycle";
import { ThemeProvider } from "./theme-provider";

type SiteProvidersProps = {
  children: React.ReactNode;
  forcedTheme?: string;
  posthogHost?: string;
  posthogKey?: string;
};

/**
 * Lightweight providers wrapper for marketing/site pages.
 *
 * Uses vanilla PostHog snippet (no React provider) to avoid loading
 * PostHog's internal React bundle that requires QueryClient.
 * Full PostHogProvider lives in (app)/layout.tsx where QueryClient exists.
 *
 * IntercomAuthLifecycle: loads after 5s or on first scroll/mousemove so Intercom is
 * injected once, supports auth identity lifecycle (update on login, shutdown on logout),
 * and duplicate widgets are avoided.
 */
export function SiteProviders({
  children,
  forcedTheme,
  posthogHost,
  posthogKey,
}: SiteProvidersProps) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      disableTransitionOnChange
      enableSystem={!forcedTheme}
      forcedTheme={forcedTheme}
    >
      <Suspense fallback={null}>{children}</Suspense>
      <IntercomAuthLifecycle />
      <PostHogVanilla posthogHost={posthogHost} posthogKey={posthogKey} />
      <Toaster richColors />
    </ThemeProvider>
  );
}

/**
 * Vanilla PostHog snippet that loads via plain script tag.
 * Zero React dependencies, no QueryClient needed.
 * Only captures pageviews and basic events.
 */
function PostHogVanilla({
  posthogHost,
  posthogKey,
}: {
  posthogHost?: string;
  posthogKey?: string;
}) {
  const apiKey = posthogKey ?? process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const apiHost = posthogHost ?? process.env.NEXT_PUBLIC_POSTHOG_HOST;

  const bootstrapScript = [
    "!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split('.');2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement('script')).type='text/javascript',p.crossOrigin='anonymous',p.async=!0,p.src=s.api_host.replace('.i.posthog.com','-assets.i.posthog.com')+'/static/array.js',(r=t.getElementsByTagName('script')[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a='posthog',u.people=u.people||[],u.toString=function(t){var e='posthog';return'posthog'!==a&&(e+='.'+a),t||(e+=' (stub)'),e},u.people.toString=function(){return u.toString(1)+'.people (stub)'},o='init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug'.split(' '),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);",
    `posthog.init(${JSON.stringify(apiKey)},{api_host:${JSON.stringify(apiHost)},person_profiles:'identified_only',capture_pageview:true,capture_pageleave:true,disable_surveys:true,advanced_disable_feature_flags:true,advanced_disable_feature_flags_on_first_load:true})`,
  ].join("");

  useEffect(() => {
    if (!apiKey || !apiHost) {
      return;
    }

    if (document.getElementById("posthog-vanilla")) {
      return;
    }

    const script = document.createElement("script");
    script.id = "posthog-vanilla";
    script.text = bootstrapScript;
    document.head.appendChild(script);
  }, [apiHost, apiKey, bootstrapScript]);

  return null;
}
